import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Collapse,
	Stack,
	Step,
	StepContent,
	StepContextType,
	StepLabel,
	Typography,
	useStepContext,
} from '@mui/material';
import { useUploadFormStepper } from '../UploadFormContext';

export interface StepWrapperProps {
	children: React.ReactNode;
	label: string;
	validLabel: string;
	valid: boolean;
	onNext?: () => void;
	onBack?: () => void;
	isFirstStep?: boolean;
	isLastStep?: boolean;
	showButtons?: boolean;
	errorMessage?: string;
}

interface UploadStepInnerProps {
	children?: React.ReactNode;
	valid: boolean;
	validLabel?: string;
	label: string;
	errorMessage?: string;
}

const UploadStepLabel: React.FC<UploadStepInnerProps> = ({ valid, errorMessage, validLabel, label }) => {
	const { index } = useStepContext() as StepContextType;
	const { activeStep, setActiveStep } = useUploadFormStepper();

	return (
		<StepLabel
			optional={
				!valid &&
				activeStep > index && (
					<Typography variant="caption" color="error">
						{errorMessage || 'An error occurred'}
					</Typography>
				)
			}
			error={!valid && activeStep > index}
			onClick={() => setActiveStep(index)}
		>
			<Stack direction="row">
				<Typography>{activeStep > index && valid ? validLabel || label : label}</Typography>
			</Stack>
		</StepLabel>
	);
};

const UploadStepContent: React.FC<UploadStepInnerProps> = ({ children, valid, errorMessage, ...props }) => {
	const { index, last } = useStepContext() as StepContextType;
	const { handleBack, handleNext, backButtonRef, continueButtonRef } = useUploadFormStepper();

	const [errorVisible, setErrorVisible] = useState(false);

	return (
		<StepContent
			slotProps={{
				transition: {
					unmountOnExit: false,
				},
			}}
		>
			<Stack gap={0} mt={2}>
				{children}

				<Box>
					{!last && props.label !== 'Final Confirmation' && (
						<Button
							variant="contained"
							onClick={() => (valid ? handleNext() : setErrorVisible(true))}
							sx={{ mt: 1, mr: 1 }}
							ref={continueButtonRef}
							size="large"
						>
							{'Continue'}
						</Button>
					)}
					{index !== 0 && props.label !== 'Final Confirmation' && (
						<Button onClick={handleBack} sx={{ mt: 1, mr: 1 }} ref={backButtonRef} variant="outlined" size="large">
							Back
						</Button>
					)}
				</Box>
				<Collapse in={errorVisible && !valid && errorMessage !== undefined} sx={{ mt: 2 }}>
					<Alert severity="error" variant="outlined">
						{`${errorMessage} before continuing`}
					</Alert>
				</Collapse>
			</Stack>
		</StepContent>
	);
};

export const UploadFormStep: React.FC<StepWrapperProps> = ({ children, valid, validLabel, label, errorMessage, ...passThroughProps }) => {
	const { stepperEnabled } = useUploadFormStepper();
	if (!stepperEnabled) {
		return children;
	}
	return (
		<Step {...passThroughProps}>
			<UploadStepLabel valid={valid} validLabel={validLabel} label={label} errorMessage={errorMessage} />
			<UploadStepContent valid={valid} label={label} errorMessage={errorMessage}>
				{children}
			</UploadStepContent>
		</Step>
	);
};
